// The board as an observable: follows the review, the board's constraints
// in the review's current scope (again whenever the scope changes, as when
// a workspace tab is switched) and the shortlist's reading, and keeps one
// `BoardView` that changes only when something the board shows changes.
// The board plugin subscribes and hands each view to Chessground; tests
// read it through `HeadlessBoard`.
import type { Chess } from '@chess/api-chess';
import { constraintScopeKey, type ConstraintRegistry } from '@chess/api-constraints';
import type { Fen, Move } from '@chess/api-model';
import { Observable } from './observable.ts';
import { positionAt, type Review, type ReviewState } from './index.ts';
import { readBoardConstraints, reviewBoardView } from './board-view.ts';
import { BoardController, type PromotionMode } from './board-input.ts';
import type { BoardArrow, BoardInput, BoardOutcome, BoardView, ShortlistReading } from './board.ts';

/** Which arrows to draw for a state; the board plugin's rule (its `arrows.ts`), plugged in. */
export type Annotate = (state: ReviewState, shortlist: ShortlistReading, chess: Chess) => readonly BoardArrow[];

export interface BoardModelDeps {
  review: Pick<Review, 'state' | 'subscribe' | 'play'>;
  chess: Chess;
  /** The constraint registry; without it the board is never locked and always draws its arrows. */
  constraints?: ConstraintRegistry;
  annotate?: Annotate;
  promotion?: PromotionMode;
}

/** A view as comparable text: two views that would draw the same board read the same. */
function keyOf(view: BoardView): string {
  return JSON.stringify({ ...view, dests: [...view.dests] });
}

export class BoardModel extends Observable<BoardView> {
  private readonly deps: BoardModelDeps;
  private readonly controller: BoardController;
  private shortlist: ShortlistReading = {};
  private legalFen?: Fen;
  private legal: readonly Move[] = [];
  private inCheck = false;
  private shownFen?: Fen;
  private key: string;
  private followed?: { key: string; stop: () => void };
  private stopReview?: () => void;

  constructor(deps: BoardModelDeps) {
    super(reviewBoardView({ state: deps.review.state(), legal: [], constraints: readBoardConstraints(undefined, undefined) }));
    this.deps = deps;
    this.controller = new BoardController({
      review: deps.review,
      view: () => this.state(),
      legal: () => this.legal,
      promotion: deps.promotion,
    });
    this.key = keyOf(this.state());
    this.stopReview = deps.review.subscribe(() => this.refresh());
    this.refresh();
  }

  /** The shortlist's latest reading; the arrows follow it while it is about the board's position. */
  setShortlist(reading: ShortlistReading): void {
    this.shortlist = reading;
    this.refresh();
  }

  /** The reader acted on the board. */
  input(input: BoardInput): BoardOutcome {
    const outcome = this.controller.input(input);
    this.refresh();
    return outcome;
  }

  /** The legal moves of the position on the board. */
  legalMoves(): readonly Move[] {
    return this.legal;
  }

  /** Stop following the review and the constraints. */
  dispose(): void {
    this.stopReview?.();
    this.stopReview = undefined;
    this.followed?.stop();
    this.followed = undefined;
  }

  // ---- internals -----------------------------------------------------------

  private refresh(): void {
    const state = this.deps.review.state();
    this.follow(state);
    const fen = positionAt(state);
    if (fen !== this.legalFen) {
      this.legalFen = fen;
      this.legal = fen ? this.deps.chess.legalMoves(fen) : [];
      this.inCheck = fen ? this.deps.chess.position(fen).check : false;
    }
    // A pick or a waiting promotion belongs to the position it was made in.
    if (fen !== this.shownFen) {
      this.shownFen = fen;
      this.controller.reset();
    }
    const constraints = readBoardConstraints(this.deps.constraints, state.scope);
    const annotate = this.deps.annotate;
    const arrows = annotate && constraints.annotationsVisible && state.game ? annotate(state, this.shortlist, this.deps.chess) : [];
    const ui = this.controller.ui();
    const view = reviewBoardView({ state, legal: this.legal, inCheck: this.inCheck, constraints, arrows, selected: ui.selected, pendingPromotion: ui.pendingPromotion });
    const key = keyOf(view);
    if (key === this.key) return;
    this.key = key;
    this.replace(view);
  }

  /** Follow the constraints of the review's scope, again when it changes. */
  private follow(state: ReviewState): void {
    const registry = this.deps.constraints;
    const scope = state.scope;
    const key = scope ? constraintScopeKey(scope) : '';
    if (!registry || this.followed?.key === key) return;
    this.followed?.stop();
    this.followed = undefined;
    if (!scope) return;
    const offArrows = registry.subscribe(scope, 'boardArrowsVisibility', () => this.refresh());
    const offMoves = registry.subscribe(scope, 'nextMove', () => this.refresh());
    this.followed = { key, stop: () => (offArrows(), offMoves()) };
  }
}
